import { useApolloClient } from '@apollo/client';
import { Stack } from '@mui/material';
import { isEmpty, isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAsyncFn } from 'react-use';

import { SectionHeaderWithAccent } from '@/components/display/SectionHeaderWithAccent/SectionHeaderWithAccent';
import { Button } from '@/components/form/baseInputs/Button';
import { EyeIcon } from '@/components/icons/EyeIcon';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import {
  FormProvider,
  useForm,
  useFormContext,
} from '@/components/react-hook-form';
import { useModalState } from '@/hooks/useModalState';
import { useReportError } from '@/hooks/useReportError';
import { useDispositiveProvisionSuggestionsContext } from '@/modules/aiDP/context/dispositiveProvisionSuggestions.context';
import { AISuggestionsMatcherProvider } from '@/modules/aiSuggestions/AISuggestionsMatcher/context/AISuggestionsMatcher.provider';
import { useUpdateSubformArrayField } from '@/modules/aiSuggestions/AISuggestionsMatcher/hooks/useUpdateSubformArrayField';
import { GeneratingIndicator } from '@/modules/aiSuggestions/components/GeneratingIndicator';
import { useEntitySuggestionsContext } from '@/modules/aiSuggestions/context/EntitySuggestions.context';
import { useSuggestionAcknowledgementContext } from '@/modules/aiSuggestions/context/SuggestionAcknowledgement.context';
import { useCanEditDispositiveProvisions } from '@/modules/authentication/hooks/useCanEditDispositiveProvisions';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useHouseholdDocumentsViewerContext } from '@/modules/documents/components/HouseholdDocumentsViewer/context/HouseholdDocumentsViewer.context';
import { EntityPreviewModal } from '@/modules/entities/components/EntityPreviewModal/EntityPreviewModal';
import { useEntityDetailsContext } from '@/modules/entities/contexts/entityDetails/entityDetails.context';
import { useEditEntitySplitScreenModalContext } from '@/modules/entities/EditEntitySplitScreen/contexts/editEntitySplitScreenModal.context';
import { ConfirmResolveDuplicatesModal } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.components';
import {
  CombinedSupportedSubformFieldTypes,
  EditEntitySection,
  isBDGSection,
  isDPSection,
} from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import {
  getArrayFieldsWithIDForSection,
  getUpdateInputFromFormValuesOrError,
  NAVIGATE_TO_ENTITY_TAB_AFTER_SAVE_PARAM,
} from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.utils';
import { EditEntitySplitScreenFooterRightActions } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreenFooter';
import { EditEntitySplitScreenFormSectionSwitcher } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreenFormSectionSwitcher';
import { EditEntitySplitScreenFormSuggestionsMatcher } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreenFormSuggestionsMatcher';
import { useUpdateEntityAndAcknowledgeSuggestionsMutation } from '@/modules/entities/EditEntitySplitScreen/graphql/editEntitySplitScreen.generated';
import { SUBMIT_ERROR_MESSAGE } from '@/modules/entities/EntitySubforms/EntitySubforms.constants';
import { useSubforms } from '@/modules/entities/EntitySubforms/useSubforms';
import { useIsGeneratingSuggestions } from '@/modules/estate/hooks/useIsGeneratingSuggestions';
import { EstateWaterfallDocument } from '@/modules/estateWaterfall/graphql/EstateWaterfall.generated';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import {
  AugmentedUpdateEntityInput,
  WorkflowTrackerKind,
} from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';

function EditEntitySplitScreenFormInner() {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const canEditHousehold = useCanEditHouseholdData();
  const canEditDP = useCanEditDispositiveProvisions();

  const client = useApolloClient();

  const [updateEntityAndSuggestions] =
    useUpdateEntityAndAcknowledgeSuggestionsMutation({
      onError: (error) => {
        showFeedback(SUBMIT_ERROR_MESSAGE);
        reportError('error in update entity mutation', error);
      },
    });

  const { householdId } = useHouseholdDetailsContext();

  const { newlyUploadedDocumentId } = useHouseholdDocumentsViewerContext();

  const {
    entityId,
    entityType,
    entitySubtypeId: subtypeId,
    displayName,
  } = useEntityDetailsContext();
  const { refetchSuggestions } = useEntitySuggestionsContext();
  const { refetchSuggestions: refetchDispositiveProvisionSuggestions } =
    useDispositiveProvisionSuggestionsContext();

  const {
    currentSection,
    currentSectionSubformConfig,
    footerRef,
    setCurrentSection,
    setClickedSubmitButton,
    handleCloseModal,
    headerRef,
    onAfterSave,
  } = useEditEntitySplitScreenModalContext();

  const canEditHouseholdData =
    canEditHousehold &&
    (!(isDPSection(currentSection) || isBDGSection(currentSection)) ||
      canEditDP);

  const handleDetailsGenerationCompleted = useCallback(() => {
    refetchSuggestions();
  }, [refetchSuggestions]);

  const handleDispositiveProvisionsGenerationCompleted = useCallback(() => {
    if (!entityId) return;

    refetchDispositiveProvisionSuggestions(entityId);
  }, [entityId, refetchDispositiveProvisionSuggestions]);

  const { isGeneratingSuggestions } = useIsGeneratingSuggestions(entityId, {
    detailsWorkflowTrackerKind:
      WorkflowTrackerKind.AiSuggestionsV2EntityDetailsGeneration,
    onDetailsCompleted: handleDetailsGenerationCompleted,
    onDispositiveProvisionsCompleted:
      handleDispositiveProvisionsGenerationCompleted,
  });

  const { acknowledgedSuggestions, previouslyAcknowledgedSuggestions } =
    useSuggestionAcknowledgementContext();

  // Accepting or rejecting a suggestion updates acknowledgedSuggestions but may
  // not mark the RHF form dirty (a reject changes no field value at all), so the
  // submit gate must also fire when there are acknowledgements not yet persisted.
  const hasUnsavedAcknowledgements = useMemo(
    () => !isEqual(acknowledgedSuggestions, previouslyAcknowledgedSuggestions),
    [acknowledgedSuggestions, previouslyAcknowledgedSuggestions]
  );

  const subforms = useSubforms(
    currentSectionSubformConfig ? [currentSectionSubformConfig] : [],
    { renderFromSplitScreen: true }
  );

  const {
    handleSubmit,
    shouldBlockNavigation,
    setShouldBlockNavigation,
    setFocus,
    formState: { isDirty: isFormStateDirty, dirtyFields },
  } = useFormContext<CombinedSupportedSubformFieldTypes>();

  // isDirty is proxied, and sometimes does not get properly updated if there are deeply-nested
  // fields.  As a workaround, check to see if the list of dirtyFields (which is not proxied)
  // is empty and determine the isDirty flag locally based on that.
  const isDirty = useMemo(
    () => isFormStateDirty || !isEmpty(dirtyFields),
    [dirtyFields, isFormStateDirty]
  );

  const { getFirstDuplicateFieldName } = useUpdateSubformArrayField();

  const [showResolveDuplicatesModal, setShowResolveDuplicatesModal] =
    useState(false);

  /**
   * Ref to track the next section to navigate to after a successful form submission.
   * Using a ref instead of state ensures we don't trigger re-renders and avoids
   * race conditions between async mutation completion and React state updates.
   * When undefined, the modal will close after submission instead of switching sections.
   */
  const pendingSectionRef = useRef<EditEntitySection | undefined>(undefined);

  /**
   * Ref to track whether we should close the modal after submission.
   * This is set to true when the user clicks "Save & close".
   */
  const shouldCloseAfterSubmitRef = useRef(false);

  /**
   * Clears the pending navigation refs to reset state after a submission
   * completes (successfully or with error) or when validation fails.
   */
  const clearPendingNavigationRefs = useCallback(() => {
    pendingSectionRef.current = undefined;
    shouldCloseAfterSubmitRef.current = false;
  }, []);

  const focusDuplicateFieldSafely = useCallback(
    (fieldName: Parameters<typeof setFocus>[0]) => {
      try {
        setFocus(fieldName);
      } catch (_error) {
        // Intentionally swallow focus failures when refs are stale/unmounted.
        return;
      }
    },
    [setFocus]
  );

  useEffect(() => {
    setShouldBlockNavigation(false);
  }, [currentSection, setShouldBlockNavigation]);

  useEffect(() => {
    if (newlyUploadedDocumentId) {
      // Submitting the DocumentUploaderForm clears the global shouldBlockNavigation state.
      // We need to set it back to true if the entity form was dirty before the document upload.
      setShouldBlockNavigation(isDirty);
    }
  }, [newlyUploadedDocumentId, setShouldBlockNavigation, isDirty]);

  /**
   * Handles navigation after a successful form submission.
   * Uses refs to determine whether to switch sections or close the modal,
   * ensuring the action only happens after the mutation completes.
   */
  const handlePostSubmitNavigation = useCallback(() => {
    const pendingSection = pendingSectionRef.current;
    const shouldClose = shouldCloseAfterSubmitRef.current;

    // Clear the refs for the next submission
    clearPendingNavigationRefs();

    diagnostics.debug(
      `[EditEntitySplitScreenForm] handlePostSubmitNavigation, pendingSection=${pendingSection}, shouldClose=${shouldClose}`
    );

    if (pendingSection) {
      // Switch to the pending section
      setCurrentSection(pendingSection);
      setShouldBlockNavigation(false);
    } else if (shouldClose) {
      // If an opener asked to land on a specific entity-details tab after save,
      // the target tab is named explicitly in the URL.
      const entityDetailsTab = searchParams.get(
        NAVIGATE_TO_ENTITY_TAB_AFTER_SAVE_PARAM
      );

      // Close first, navigate last. Closing can touch the URL (clearing the
      // after-save param / stripping modalOpen), so the navigation must run
      // last to win — otherwise a concurrent setSearchParams would revert the
      // tab jump. The navigation target already omits the after-save param.
      handleCloseModal();

      if (entityDetailsTab && householdId && entityId) {
        navigate(
          getCompletePathFromRouteKey(
            ROUTE_KEYS.HOUSEHOLD_ENTITY_DETAILS,
            {
              householdId,
              entityId,
            },
            { entityTab: entityDetailsTab }
          )
        );
      }
    }
  }, [
    clearPendingNavigationRefs,
    entityId,
    handleCloseModal,
    householdId,
    navigate,
    searchParams,
    setCurrentSection,
    setShouldBlockNavigation,
  ]);

  /**
   * Submits the update entity mutation.
   * @param input - The mutation input
   * @param onCompleteOverride - Optional callback to run instead of the default
   *   ref-based navigation. Use this for custom post-save actions like opening
   *   the preview modal.
   */
  const submitUpdateEntityMutation = useCallback(
    async (
      input: AugmentedUpdateEntityInput,
      onCompleteOverride?: () => void
    ) => {
      return await updateEntityAndSuggestions({
        variables: {
          entityInput: input,
          acknowledgeSuggestionsInput: Object.values(
            acknowledgedSuggestions
          ).map((s) => ({
            suggestionID: s.suggestionID,
            status: s.status,
            clientProfileID: s.clientProfileId,
            clientOrganizationID: s.clientOrganizationId,
            entityID: s.entityId,
          })),
        },
        onCompleted: async (data) => {
          showFeedback('Entity updated successfully', {
            variant: 'success',
          });

          await client.refetchQueries({
            include: [EstateWaterfallDocument],
            updateCache(cache) {
              cache.evict({ id: cache.identify(data.updateEntity) });
              cache.evict({ fieldName: 'entities' });
              cache.gc();
            },
          });

          // Let a host refresh its own data on save (e.g. the presentation
          // designer refetches the slide), so it doesn't have to refetch on
          // every close.
          onAfterSave?.();

          // Use override callback if provided, otherwise use ref-based navigation
          if (onCompleteOverride) {
            onCompleteOverride();
          } else {
            handlePostSubmitNavigation();
          }
        },
        onError: () => {
          // Clear the refs on error so we don't have stale navigation state
          clearPendingNavigationRefs();
          // Reset the clicked button since submission failed
          setClickedSubmitButton(null);
        },
      });
    },
    [
      acknowledgedSuggestions,
      clearPendingNavigationRefs,
      client,
      handlePostSubmitNavigation,
      onAfterSave,
      setClickedSubmitButton,
      showFeedback,
      updateEntityAndSuggestions,
    ]
  );

  /**
   * Validates and submits the form.
   * @param values - The form values
   * @param onCompleteOverride - Optional callback to run instead of the default
   *   ref-based navigation. Use this for custom post-save actions.
   */
  const onValidSubmission = useCallback(
    async (
      values: CombinedSupportedSubformFieldTypes,
      onCompleteOverride?: () => void
    ) => {
      if (!canEditHouseholdData) return;
      if (!entityId || !entityType || !subtypeId || !householdId) {
        throw new Error(
          'Missing required entityId, entityType, subtypeId, or householdId'
        );
      }

      const result = getUpdateInputFromFormValuesOrError(
        {
          entityType,
          subtypeId,
          entityId,
          householdId,
        },
        values
      );

      if (result.input) {
        return submitUpdateEntityMutation(result.input, onCompleteOverride);
      }

      // Validation failed - clear the refs and reset button state
      clearPendingNavigationRefs();
      setClickedSubmitButton(null);

      if (result.validationError) {
        showFeedback(result.validationError.message);
        return Promise.resolve();
      }

      showFeedback(SUBMIT_ERROR_MESSAGE);
      reportError(
        'unknown error when validating update entity form values',
        result.error
      );
    },
    [
      canEditHouseholdData,
      clearPendingNavigationRefs,
      entityId,
      entityType,
      householdId,
      reportError,
      setClickedSubmitButton,
      showFeedback,
      submitUpdateEntityMutation,
      subtypeId,
    ]
  );

  const handleSubmitAndClose = useCallback(() => {
    // Set up the refs to close the modal after submission
    pendingSectionRef.current = undefined;
    shouldCloseAfterSubmitRef.current = true;

    flushSync(() => {
      setShouldBlockNavigation(false);
    });

    void handleSubmit(
      (values) => {
        return onValidSubmission(values);
      },
      () => {
        // Validation failed - clear refs and restore navigation blocking
        clearPendingNavigationRefs();
        setShouldBlockNavigation(isDirty);
      }
    )();
  }, [
    clearPendingNavigationRefs,
    handleSubmit,
    isDirty,
    onValidSubmission,
    setShouldBlockNavigation,
  ]);

  const handleSubmitAndSwitchSection = useCallback(
    (newSection: EditEntitySection) => {
      // Set up the ref to switch to the new section after submission
      pendingSectionRef.current = newSection;
      shouldCloseAfterSubmitRef.current = false;

      flushSync(() => {
        setShouldBlockNavigation(false);
      });

      void handleSubmit(
        (values) => {
          return onValidSubmission(values);
        },
        () => {
          // Validation failed - clear refs and restore navigation blocking
          clearPendingNavigationRefs();
          setShouldBlockNavigation(isDirty);
        }
      )();
    },
    [
      clearPendingNavigationRefs,
      handleSubmit,
      isDirty,
      onValidSubmission,
      setShouldBlockNavigation,
    ]
  );

  const handlePreSubmit = useCallback(
    (afterSubmitSection?: EditEntitySection) => {
      diagnostics.debug(
        `[EditEntitySplitScreenForm] handling EditEntitySplitScreen footer submit, afterSubmitSection=${afterSubmitSection}, shouldBlockNavigation=${shouldBlockNavigation}, isDirty=${isDirty}`
      );

      const hasNothingToSubmit =
        !isDirty && !shouldBlockNavigation && !hasUnsavedAcknowledgements;

      if (hasNothingToSubmit || !canEditHouseholdData) {
        // If there weren't any form updates, just switch sections or close the
        // modal without submitting.
        if (afterSubmitSection) {
          setCurrentSection(afterSubmitSection);
        } else {
          handleCloseModal();
        }
        return;
      }

      const currentSectionFieldsToCheck = getArrayFieldsWithIDForSection(
        entityType,
        currentSection
      );

      const firstDuplicate = getFirstDuplicateFieldName(
        currentSectionFieldsToCheck
      );

      // If there are duplicates, show the modal and focus on the first one.
      if (firstDuplicate) {
        setShowResolveDuplicatesModal(true);
        focusDuplicateFieldSafely(firstDuplicate);
        // Reset the clicked button since we're showing a modal instead of submitting
        setClickedSubmitButton(null);
        return;
      }

      // No duplicates. Carry on with the submission.
      if (afterSubmitSection) {
        // If there's a requested section, switch to it.
        handleSubmitAndSwitchSection(afterSubmitSection);
      } else {
        // Otherwise, just close the modal.
        handleSubmitAndClose();
      }
    },
    [
      shouldBlockNavigation,
      isDirty,
      hasUnsavedAcknowledgements,
      canEditHouseholdData,
      entityType,
      currentSection,
      getFirstDuplicateFieldName,
      focusDuplicateFieldSafely,
      setCurrentSection,
      handleCloseModal,
      setClickedSubmitButton,
      handleSubmitAndSwitchSection,
      handleSubmitAndClose,
    ]
  );

  const handleCloseResolveDuplicatesModal = useCallback(() => {
    setShowResolveDuplicatesModal(false);
  }, []);

  const [
    { isModalOpen: isPreviewModalOpen },
    { openModal: openPreviewModal, closeModal: closePreviewModal },
  ] = useModalState();

  /**
   * Handles submitting and opening the preview modal.
   * Uses onCompleteOverride to open the preview modal after saving instead
   * of the default ref-based navigation.
   */
  const handleOpenPreviewModal = useCallback(async () => {
    if (!canEditHouseholdData) {
      openPreviewModal();
      return;
    }
    await handleSubmit((values) => {
      return onValidSubmission(values, openPreviewModal);
    })();
  }, [canEditHouseholdData, handleSubmit, onValidSubmission, openPreviewModal]);

  const [{ loading: isPreviewModalLoading }, handleOpenPreviewModalFn] =
    useAsyncFn(async () => handleOpenPreviewModal(), [handleOpenPreviewModal]);

  return (
    <>
      {headerRef.current &&
        createPortal(
          <SectionHeaderWithAccent
            containerProps={{
              pt: 1.5,
              pb: 1.5,
              pl: 2,
              pr: 2,
            }}
            header={{
              heading: displayName,
            }}
            rightHeaderContent={
              <Stack direction="row" spacing={1} alignItems="center">
                {isGeneratingSuggestions && (
                  <GeneratingIndicator dataTestId="edit-entity-split-screen" />
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  startIcon={EyeIcon}
                  loading={isPreviewModalLoading}
                  onClick={() => handleOpenPreviewModalFn()}
                >
                  Preview deliverable
                </Button>
                <EditEntitySplitScreenFormSectionSwitcher
                  onSubmit={handlePreSubmit}
                />
              </Stack>
            }
          />,
          headerRef.current
        )}
      {canEditHouseholdData && <EditEntitySplitScreenFormSuggestionsMatcher />}
      {subforms?.[0]}
      {footerRef.current &&
        createPortal(
          <EditEntitySplitScreenFooterRightActions
            onSubmit={handlePreSubmit}
          />,
          footerRef.current
        )}
      {isPreviewModalOpen && entityId && householdId && (
        <EntityPreviewModal
          entityId={entityId || ''}
          householdId={householdId || ''}
          onClose={closePreviewModal}
        />
      )}
      <ConfirmResolveDuplicatesModal
        isOpen={showResolveDuplicatesModal}
        onClose={handleCloseResolveDuplicatesModal}
      />
    </>
  );
}

interface EditEntitySplitScreenFormProps {
  initialSubformValues: CombinedSupportedSubformFieldTypes;
}

export function EditEntitySplitScreenForm({
  initialSubformValues,
}: EditEntitySplitScreenFormProps) {
  const formMethods = useForm<CombinedSupportedSubformFieldTypes>(
    { defaultValues: initialSubformValues },
    'entity-editor'
  );

  return (
    <Stack
      direction="column"
      height="100%"
      p={2}
      gap={2}
      sx={{ overflowY: 'scroll' }}
    >
      <FormProvider {...formMethods}>
        <AISuggestionsMatcherProvider>
          <EditEntitySplitScreenFormInner />
        </AISuggestionsMatcherProvider>
      </FormProvider>
    </Stack>
  );
}
